import { LIMITS, normaliseLobbyCode, normaliseUsername, type GameType } from '@cardengine/shared';
import { useEffect, useState } from 'react';
import { randomAlias } from '../aliases.js';
import { useStore } from '../store.js';

type Mode = 'menu' | 'create' | 'join';

/** A ?join=CODE link drops the player straight into the join form. */
function codeFromUrl(): string {
  const params = new URLSearchParams(window.location.search);
  return normaliseLobbyCode(params.get('join')) ?? '';
}

/**
 * Sit-down panel: choose a name, then start a table or join one by code.
 * Lives on a game's page, so the game being joined is implicit.
 *
 * `gameType` is passed explicitly rather than left implicit. It used not to be:
 * while Switch was the only game with a table, `createLobby` could be called
 * with nothing and the server's default was right every time. The moment a
 * second seated game arrived that default became silently wrong — the noughts
 * and crosses page cheerfully opened a Switch table, with no error anywhere to
 * say so.
 *
 * Joining does not need it. A code identifies one table, and that table already
 * knows what it is playing.
 */
export function JoinPanel({ gameName, gameType }: { gameName: string; gameType: GameType }) {
  const username = useStore((s) => s.username);
  const busy = useStore((s) => s.busy);
  const homeError = useStore((s) => s.homeError);
  const createLobby = useStore((s) => s.createLobby);
  const joinLobby = useStore((s) => s.joinLobby);
  const setHomeError = useStore((s) => s.setHomeError);

  const initialCode = codeFromUrl();
  const [mode, setMode] = useState<Mode>(initialCode ? 'join' : 'menu');
  const [name, setName] = useState(username);
  const [code, setCode] = useState(initialCode);

  useEffect(() => {
    setName((current) => current || username);
  }, [username]);

  const nameValid = normaliseUsername(name) !== null;
  const codeValid = normaliseLobbyCode(code) !== null;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!nameValid) {
      setHomeError('Pick a name of 1-16 letters, numbers or spaces.');
      return;
    }
    if (mode === 'create') {
      await createLobby(name.trim(), undefined, gameType);
    } else if (codeValid) {
      await joinLobby(name.trim(), code);
    }
  }

  return (
    <div className="joinpanel">
      {mode === 'menu' ? (
        <div className="joinpanel__choices">
          <button type="button" className="btn btn--primary btn--large" onClick={() => setMode('create')}>
            Start a table
          </button>
          <button type="button" className="btn btn--secondary btn--large" onClick={() => setMode('join')}>
            Join with a code
          </button>
          <p className="joinpanel__note">2 minutes to learn. No sign-up.</p>
        </div>
      ) : (
        <form className="joinpanel__form" onSubmit={submit}>
          <p className="joinpanel__heading">
            {mode === 'create' ? `Start a table of ${gameName}` : `Join a table of ${gameName}`}
          </p>

          {/* A div, not a label: a button inside a label is activated twice —
              once itself, once by the label — and reads ambiguously to a
              screen reader. The label points at the input by id instead. */}
          <div className="field">
            <span className="field__row">
              <label className="field__label" htmlFor="join-name">
                Your name
              </label>
              {/* An invite code can travel further than the people you sent it
                  to, and this name is all anyone at the table sees. */}
              <button
                type="button"
                className="field__action"
                onClick={() => setName((current) => randomAlias(current))}
              >
                Use a nickname
              </button>
            </span>
            <input
              id="join-name"
              className="field__input"
              value={name}
              onChange={(event) => setName(event.target.value)}
              maxLength={LIMITS.usernameMax}
              placeholder="e.g. Quiet Magpie"
              autoFocus={!initialCode}
              autoComplete="nickname"
            />
          </div>

          {mode === 'join' && (
            <label className="field">
              <span className="field__label">Table code</span>
              <input
                className="field__input field__input--code"
                value={code}
                onChange={(event) => setCode(event.target.value.toUpperCase())}
                maxLength={LIMITS.lobbyCodeLength}
                placeholder="ABC123"
                autoFocus={Boolean(initialCode)}
                autoCapitalize="characters"
                spellCheck={false}
              />
            </label>
          )}

          {homeError && <p className="joinpanel__error">{homeError}</p>}

          <div className="joinpanel__actions">
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => {
                setMode('menu');
                setHomeError(null);
              }}
            >
              Back
            </button>
            <button
              type="submit"
              className="btn btn--primary"
              disabled={busy || !nameValid || (mode === 'join' && !codeValid)}
            >
              {busy ? 'Please wait…' : mode === 'create' ? 'Start table' : 'Join table'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
